import { useState, ReactNode, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  CollisionDetection,
} from '@dnd-kit/core';
import { BookmarkDragOverlay, CollectionDragOverlay } from './DragOverlays';
import type { BookmarkWithTags, CollectionWithCount } from '@/types/database';

export type DragItemType = 'bookmark' | 'collection';

export interface DragData {
  type: DragItemType;
  bookmark?: BookmarkWithTags;
  collection?: CollectionWithCount;
}

interface DndProviderProps {
  children: ReactNode;
  onBookmarkMove: (bookmarkId: string, collectionId: string | null) => void;
  onCollectionReorder: (activeId: string, overId: string) => void;
}

export function DndProvider({
  children,
  onBookmarkMove,
  onCollectionReorder,
}: DndProviderProps) {
  const [activeData, setActiveData] = useState<DragData | null>(null);

  // Custom collision detection:
  // - Bookmark drag: pointerWithin (must drop inside collection box)
  // - Collection drag: closestCenter (for smooth reordering)
  const customCollisionDetection: CollisionDetection = useCallback(
    (args) => {
      if (activeData?.type === 'bookmark') {
        // For bookmarks, require pointer to be within the drop target
        return pointerWithin(args);
      }
      // For collections, use closest center for smooth sorting
      return closestCenter(args);
    },
    [activeData?.type]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevent accidental drags
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as DragData | undefined;
    if (data) {
      setActiveData(data);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveData(null);
      return;
    }

    const activeData = active.data.current as DragData | undefined;
    const overData = over.data.current as DragData | undefined;

    // Bookmark dropped on collection (or "all-bookmarks")
    if (activeData?.type === 'bookmark') {
      if (overData?.type === 'collection') {
        // Drop on a specific collection
        onBookmarkMove(active.id as string, over.id as string);
      } else if (over.id === 'all-bookmarks') {
        // Drop on "All bookmarks" - remove from collection
        onBookmarkMove(active.id as string, null);
      }
    }

    // Collection reordered
    if (activeData?.type === 'collection' && overData?.type === 'collection') {
      if (active.id !== over.id) {
        onCollectionReorder(active.id as string, over.id as string);
      }
    }

    setActiveData(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {children}
      <DragOverlay dropAnimation={null}>
        {activeData?.type === 'bookmark' && activeData.bookmark && (
          <BookmarkDragOverlay bookmark={activeData.bookmark} />
        )}
        {activeData?.type === 'collection' && activeData.collection && (
          <CollectionDragOverlay collection={activeData.collection} />
        )}
      </DragOverlay>
    </DndContext>
  );
}
